// frontend/src/pages/SignupPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import './LoginPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PENDING_KEY = 'pendingApprovalEmail';

const SignupPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [verifiedMemberId, setVerifiedMemberId] = useState('');

  const [identityForm, setIdentityForm] = useState({
    memberId: '',
    phoneNumber: '',
    idNumber: ''
  });

  const [accountForm, setAccountForm] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Step 1 handlers
  const handleIdentityChange = (e) => {
    const { name, value } = e.target;
    setIdentityForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const validateIdentityForm = () => {
    const newErrors = {};
    if (!identityForm.memberId.trim()) newErrors.memberId = 'Member ID is required';
    if (!identityForm.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!identityForm.idNumber.trim()) newErrors.idNumber = 'ID number is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyIdentity = async (e) => {
    e.preventDefault();
    setGeneralError('');
    if (!validateIdentityForm()) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/verify-member`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberId: identityForm.memberId.trim(),
          phoneNumber: identityForm.phoneNumber.trim(),
          idNumber: identityForm.idNumber.trim()
        })
      });

      const data = await response.json();

      if (data.success && data.verified) {
        setVerifiedMemberId(data.memberId);
        setStep(2);
      } else if (data.error === 'account_exists') {
        setGeneralError('An account already exists for this Member ID. Please log in or contact the office.');
      } else {
        setGeneralError(
          'The details you entered do not match our records. Please check your Member ID, phone number, and ID number. If the problem persists, contact the office.'
        );
      }
    } catch {
      setGeneralError('Unable to reach the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2 handlers
  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const validateAccountForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!accountForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(accountForm.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!accountForm.password) {
      newErrors.password = 'Password is required';
    } else if (accountForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (!accountForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (accountForm.password !== accountForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setGeneralError('');
    if (!validateAccountForm()) return;

    setIsLoading(true);
    const cleanEmail = accountForm.email.trim();
    const cleanEmailLower = cleanEmail.toLowerCase();

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, accountForm.password);
      const uid = userCredential.user.uid;

      await setDoc(doc(db, 'users', uid), {
        uid,
        email: cleanEmail,
        emailLower: cleanEmailLower,
        displayName: verifiedMemberId,
        role: 'member',
        memberId: verifiedMemberId,
        approved: false,
        isActive: false,
        createdAt: Date.now(),
        status: 'pending'
      });

      await auth.signOut();

      try { localStorage.setItem(PENDING_KEY, cleanEmailLower); } catch {}

      setSuccessMessage(
        'Account created! Your account must be approved by an administrator before you can sign in. Redirecting to login...'
      );

      setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { pending: true, justSignedUp: true, email: cleanEmailLower }
        });
      }, 2500);

    } catch (error) {
      let msg = 'Signup failed. Please try again.';
      if (error.code === 'auth/email-already-in-use') msg = 'This email is already registered. Please sign in instead.';
      else if (error.code === 'auth/invalid-email') msg = 'Invalid email address format.';
      else if (error.code === 'auth/weak-password') msg = 'Password is too weak. Please use at least 6 characters.';
      else if (error.message) msg = error.message;
      setGeneralError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <div className="login-logo">
              <span className="logo-icon">&#x1F4B0;</span>
            </div>
            <h1>Hope SACCO</h1>
            <p>{step === 1 ? 'Verify Your Identity' : 'Create Your Account'}</p>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '20px', fontSize: '13px', color: '#888' }}>
            Step {step} of 2 &mdash; {step === 1 ? 'Identity Verification' : 'Member ID: ' + verifiedMemberId}
          </div>

          {successMessage && (
            <div className="alert alert-success">
              <span className="alert-icon">&#x2713;</span>
              <span>{successMessage}</span>
            </div>
          )}

          {generalError && (
            <div className="alert alert-error">
              <span className="alert-icon">&#x26A0;&#xFE0F;</span>
              <span>{generalError}</span>
            </div>
          )}

          {step === 1 && (
            <>
              <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                <span className="alert-icon">&#x2139;&#xFE0F;</span>
                <span>Enter your Member ID, phone number, and ID number exactly as registered with the SACCO.</span>
              </div>

              <form onSubmit={handleVerifyIdentity} className="login-form" noValidate>
                <div className="form-group">
                  <label htmlFor="memberId">Member ID <span className="required">*</span></label>
                  <input
                    type="text"
                    id="memberId"
                    name="memberId"
                    value={identityForm.memberId}
                    onChange={handleIdentityChange}
                    className={errors.memberId ? 'input-error' : ''}
                    placeholder="e.g. S00564"
                    disabled={isLoading}
                    autoFocus
                  />
                  {errors.memberId && <span className="error-message">{errors.memberId}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={identityForm.phoneNumber}
                    onChange={handleIdentityChange}
                    className={errors.phoneNumber ? 'input-error' : ''}
                    placeholder="e.g. 0733123456"
                    disabled={isLoading}
                  />
                  {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="idNumber">National ID Number <span className="required">*</span></label>
                  <input
                    type="text"
                    id="idNumber"
                    name="idNumber"
                    value={identityForm.idNumber}
                    onChange={handleIdentityChange}
                    className={errors.idNumber ? 'input-error' : ''}
                    placeholder="Enter your ID number"
                    disabled={isLoading}
                  />
                  {errors.idNumber && <span className="error-message">{errors.idNumber}</span>}
                </div>

                <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
                  {isLoading ? <><span className="spinner"></span>Verifying...</> : 'Verify Identity'}
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <form onSubmit={handleCreateAccount} className="login-form" noValidate>
              <div className="form-group">
                <label htmlFor="email">Email Address <span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={accountForm.email}
                  onChange={handleAccountChange}
                  className={errors.email ? 'input-error' : ''}
                  placeholder="Enter your email"
                  disabled={isLoading}
                  autoFocus
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password <span className="required">*</span></label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={accountForm.password}
                  onChange={handleAccountChange}
                  className={errors.password ? 'input-error' : ''}
                  placeholder="Minimum 6 characters"
                  disabled={isLoading}
                />
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password <span className="required">*</span></label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={accountForm.confirmPassword}
                  onChange={handleAccountChange}
                  className={errors.confirmPassword ? 'input-error' : ''}
                  placeholder="Re-enter your password"
                  disabled={isLoading}
                />
                {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
              </div>

              <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
                {isLoading ? <><span className="spinner"></span>Creating account...</> : 'Create Account'}
              </button>

              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => { setStep(1); setGeneralError(''); setErrors({}); }}
                  disabled={isLoading}
                >
                  &larr; Back
                </button>
              </div>
            </form>
          )}

          <div className="login-footer" style={{ marginTop: '20px' }}>
            <button type="button" onClick={() => navigate('/login')} className="link-button" disabled={isLoading}>
              Already have an account? Sign in
            </button>
          </div>

          <div className="support-info">
            <p><strong>Need help? Contact the office:</strong></p>
            <p>&#x1F4E7; support@hopesacco.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;